/**
 * Medchem Design — Meanwell-rule-driven action definitions
 *
 * Actions that execute the design moves Meanwell's rules recommend:
 *   - scan-structural-alerts: SMARTS-based toxicophore filter (IMPLEMENTED)
 *   - replace-with-bioisostere: apply a cataloged bioisosteric swap
 *   - mitigate-toxicophore: apply the mitigation recipe for a detected alert
 *   - swap-heteroaromatic-ring: swap phenyl↔pyridine↔thiophene↔thiazole etc.
 *   - fluorinate-at-position: add F at a specified atom or SMARTS site
 *
 * All rule knowledge lives in `gibbs/knowledge/medchemRules.js`. These actions
 * are the executable surface the LLM / user can fire.
 *
 * Gap-analysis: labwork/studies/meanwell-medchem-knowledge/gap-analysis.md
 *
 * @module gibbs/registry/actions/medchem-design
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── scan-structural-alerts (implemented — pure SMARTS filter) ─────────────

export const scanStructuralAlerts = {
    id: 'scan-structural-alerts',
    name: 'Scan for Structural Alerts (Toxicophores)',
    category: 'Medchem Design',
    description:
        'Scan the active compound(s) for known toxicophores (aniline, furan, thiophene, 2-aminothiazole, hydrazine, Michael acceptor, quinone-imine precursor, nitroaromatic, thiazolidinedione). Returns each matched alert with its mechanism and mitigation strategy, citing Meanwell 2023 and Stepan/Kalgutkar 2011.',
    inputs: [
        param('compounds', `${T.Compound}[]`, 'Compounds to scan'),
    ],
    outputs: [
        param('alerts', 'object[]', 'Per-compound list of matched toxicophores with mitigation'),
    ],
    parameters: [
        param('includeMitigation', 'boolean', 'Include mitigation strategies in output', false, { defaultValue: true }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ScanStructuralAlerts',
    serverCommand: null,
    jobType: null,
};

// ─── replace-with-bioisostere (planned) ─────────────────────────────────────

export const replaceWithBioisostere = {
    id: 'replace-with-bioisostere',
    name: 'Replace Substructure with Bioisostere',
    category: 'Medchem Design',
    description:
        'Apply a cataloged bioisosteric replacement (Meanwell 2011 J. Med. Chem.) to the active compound. Supports COOH→tetrazole/acyl sulfonamide, amide→oxadiazole/triazole, phenyl→pyridine/thiophene, ester→amide, and other classical and non-classical isosteres. Returns one or more analog structures. PREFERRED INPUT: name a `ruleId` from the medchem catalog (e.g., `iso_cooh_tetrazole`) — the SMARTS pattern and replacement fragment are auto-resolved from the rule. Only supply `fromPattern` + `toFragment` directly for custom/uncataloged transforms.',
    inputs: [
        param('compound', T.Compound, 'Compound to modify'),
        // fromPattern / toFragment are NOT required when ruleId is given —
        // the handler resolves them from the catalog at dispatch time. Marking
        // them required:false here prevents the validator from prompting the
        // user for SMARTS strings when a ruleId is the natural specifier
        // (the 2026-05-14 Bill walkthrough surfaced this: clicking a
        // bioisostere clarification option fired a SMARTS prompt at the user).
        param('fromPattern', 'string', 'SMARTS pattern of the substructure to replace (auto-resolved when ruleId is set)', false),
        param('toFragment', T.SMILES, 'Replacement fragment SMILES (auto-resolved when ruleId is set)', false),
    ],
    outputs: [
        param('analogs', `${T.Compound}[]`, 'Compounds with the isostere applied'),
    ],
    parameters: [
        param('ruleId', 'string', 'Meanwell rule id from the catalog (preferred over fromPattern/toFragment)', false),
        param('maxAnalogs', 'number', 'Maximum analogs to generate', false, { defaultValue: 3 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compound must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'New analog compounds added to the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ReplaceWithBioisostere',
    serverCommand: null,
    jobType: null,
};

// ─── mitigate-toxicophore ──────────────────────────────────────────────────

export const mitigateToxicophore = {
    id: 'mitigate-toxicophore',
    name: 'Apply Toxicophore Mitigation',
    category: 'Medchem Design',
    description:
        'Apply the mitigation recipe for a detected structural alert. Example mitigations: aniline → ortho-block or N→C swap; furan → oxazole/thiazole replacement or 2,5-disubstitution; 2-aminothiazole → 2-methyl-thiadiazole. Should be run after scan-structural-alerts detects an alert.',
    inputs: [
        param('compound', T.Compound, 'Compound containing the alert'),
        param('alertType', 'string', 'Alert identifier (e.g., aniline, furan, 2-aminothiazole)'),
    ],
    outputs: [
        param('analogs', `${T.Compound}[]`, 'Mitigated analogs'),
    ],
    parameters: [
        param('mitigationStrategy', 'string', 'Strategy id (ortho-block, EWG, heteroatom-swap, N-to-C)', false),
    ],
    dependsOn: ['scan-structural-alerts'],
    preconditions: [
        pre('compound_loaded', 'Compound must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'Mitigated analogs added to the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'MitigateToxicophore',
    serverCommand: null,
    jobType: null,
};

// ─── swap-heteroaromatic-ring ─────────────────────────────────────────────

export const swapHeteroaromaticRing = {
    id: 'swap-heteroaromatic-ring',
    name: 'Swap Heteroaromatic Ring',
    category: 'Medchem Design',
    description:
        'Exchange one heteroaromatic ring class for another (phenyl↔pyridine↔pyrimidine↔thiophene↔thiazole↔furan↔pyrazole↔imidazole). Preserves substitution pattern and connection geometry where possible. Generates analog set for SAR exploration. Rules from Meanwell 2017 Adv. Het. Chem.',
    inputs: [
        param('compound', T.Compound, 'Compound with the ring to swap'),
        param('sourceRing', 'string', 'Current ring class (e.g., "phenyl")'),
        param('targetRing', 'string', 'Desired ring class (e.g., "pyridine-3")'),
    ],
    outputs: [
        param('analogs', `${T.Compound}[]`, 'Compounds with the swapped ring'),
    ],
    parameters: [
        param('preserveSubstitution', 'boolean', 'Keep existing substitution pattern', false, { defaultValue: true }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compound must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'Analog compounds added to the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'SwapHeteroaromaticRing',
    serverCommand: null,
    jobType: null,
};

// ─── fluorinate-at-position ───────────────────────────────────────────────

export const fluorinateAtPosition = {
    id: 'fluorinate-at-position',
    name: 'Fluorinate at Specified Position',
    category: 'Medchem Design',
    description:
        'Add fluorine to a compound at a specified atom index or SMARTS-matched position. Supports F, CF3, OCF3, difluoromethyl. Strategies from Gillis & Meanwell 2015: block CYP metabolism, lower amine pKa (β-F ~1.7 per F), enforce gauche conformation, enable multipolar interactions.',
    inputs: [
        param('compound', T.Compound, 'Compound to fluorinate'),
        param('positionPattern', 'string', 'SMARTS pattern or atom-index JSON for placement'),
    ],
    outputs: [
        param('analogs', `${T.Compound}[]`, 'Fluorinated analogs'),
    ],
    parameters: [
        param('fluorineGroup', 'string', 'Group to attach (F, CF3, OCF3, CHF2)', false, { defaultValue: 'F', options: ['F', 'CF3', 'OCF3', 'CHF2'] }),
        param('strategy', 'string', 'Design rationale', false, { options: ['block-metabolism', 'lower-pka', 'gauche', 'multipolar'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compound must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'Fluorinated analogs added to the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FluorinateAtPosition',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    scanStructuralAlerts,
    replaceWithBioisostere,
    mitigateToxicophore,
    swapHeteroaromaticRing,
    fluorinateAtPosition,
];
